import React from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

const MAX_FILE_SIZE = 10.9 * 1024 * 1024;

type ImageUploaderProps = {
  label: string;
  onFileUpload: (file: File | null) => void;
  uploadedFile: File | null;
  errorMessage?: string | null;
};

export const ImageUploader: React.FC<ImageUploaderProps> = ({ label, onFileUpload, uploadedFile, errorMessage }) => {
  const { t } = useTranslation();

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFileUpload(acceptedFiles[0]);
      }
    },
    maxSize: MAX_FILE_SIZE,
    accept: { "image/*": [] },
    multiple: false,
  });

  return (
    <div className="mb-4 rounded-md border-2 border-dashed p-4">
      <div {...getRootProps()} className="bg-gray-50 hover:bg-gray-100 cursor-pointer px-4 py-8 text-center transition">
        <input {...getInputProps()} />
        {uploadedFile ? (
          <p className="text-gray-800 text-sm font-medium">{uploadedFile.name}</p>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <svg
              className="text-blue-600 h-6 w-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            <p className="text-blue-600 font-semibold">{label}</p>
            <p className="text-gray-500 text-xs">
              {t("intakeInsuranceInfo.imageFileTypes")}
              <br />
              {t("intakeInsuranceInfo.imageMaxSize")}
            </p>
          </div>
        )}
      </div>
      {fileRejections.length > 0 && <p className="text-red">{t("intakeInsuranceInfo.invalidImageError")}</p>}
      {errorMessage && <p className="text-red">{errorMessage}</p>}
    </div>
  );
};
